import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";

import { getCategoriesRequest, getSeniorHomesRequest, updateGroupRequest, updateUserRequest } from "../redux/actions";
import { AppState } from "../redux/types";
import { localePath } from "../i18n/config";
import { USER_TYPE } from "../utils/constants";
import Loader from "./Loader";
import Message from "./Message";

interface FormData {
    id?: string;
    username: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
    nursingHome: string;
    department: string;
    homeAddress: string;
    homeCity: string;
    homePostalCode: string;
    nursingHomeAddress: string;
    role: string;
}

const SeniorDetail = ({ onSelect }: { onSelect: (value: string) => void }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const user = useSelector((state: AppState) => state.user);
    const { error, loading, isLoggedIn } = user;
    const categoryData = useSelector((state: AppState) => state.categories.category);
    const relativeCreateEmailId = useSelector((state: AppState) => categoryData?.attributes.RelativeCreateAccount);
    const customerSeniorEmailId = useSelector((state: AppState) => categoryData?.attributes.customerSeniorEmailId);
    const category = useSelector((state: AppState) => categoryData?.attributes.slug);

    useEffect(() => {
        if (!categoryData && user.userType === USER_TYPE.appOnly) {
            dispatch(getCategoriesRequest("app"));
        }
    }, [categoryData, dispatch, user.userType]);

    // Select senior from group or user itself
    const senior = user.role === "senior" ? user : user.group?.members?.find((m) => m.role === "senior");

    const [formData, setFormData] = useState<FormData>({
        id: senior?.id,
        username: senior?.username || "",
        password: "",
        confirmPassword: "",
        firstName: senior?.firstName || "",
        lastName: senior?.lastName || "",
        mobileNumber: senior?.mobileNumber || "",
        nursingHome: senior?.nursingHome || "",
        department: senior?.department || "",
        homeAddress: senior?.homeAddress || "",
        homeCity: senior?.homeCity || "",
        homePostalCode: senior?.homePostalCode || "",
        nursingHomeAddress: senior?.nursingHomeAddress || "",
        role: "senior",
    });

    const [btnDisabled, setBtnDisabled] = useState(true);
    const [message, setMessage] = useState("");
    const [saved, setSaved] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showPass2, setShowPass2] = useState(false);

    useEffect(() => {
        if (!isLoggedIn) {
            history.push(localePath("kirjaudu"));
            return;
        }
        if (user.id) {
            dispatch(getSeniorHomesRequest());
        }
    }, [dispatch, history, isLoggedIn, user.id]);

    // Update form data when senior changes
    useEffect(() => {
        if (senior) {
            setFormData((prev) => ({
                ...prev,
                id: senior.id,
                username: senior.username || "",
                firstName: senior.firstName || "",
                lastName: senior.lastName || "",
                mobileNumber: senior.mobileNumber || "",
                nursingHome: senior.nursingHome || "",
                department: senior.department || "",
                homeAddress: senior.homeAddress || "",
                homeCity: senior.homeCity || "",
                homePostalCode: senior.homePostalCode || "",
                nursingHomeAddress: senior.nursingHomeAddress || "",
            }));
        }
    }, [senior]);

    // Validate form fields to enable submit button and set message
    useEffect(() => {
        let disable = false;
        if (user.userType === USER_TYPE.appOnly) {
            disable = !(formData.firstName.trim() && formData.lastName.trim());
        } else {
            disable =
                !formData.firstName ||
                !formData.lastName ||
                !formData.department.length ||
                (formData.department === "nursingHome" && !formData.nursingHome && !formData.nursingHomeAddress) ||
                (formData.department === "homeAddress" && !formData.homeAddress && !formData.homeCity && !formData.homePostalCode);
        }
        setBtnDisabled(disable);

        if (!saved) {
            let msg = "";
            if (formData.password.length > 0 && formData.password.length < 11) {
                msg = "Salasana on liian lyhyt";
            } else if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
                msg = "Salasanat eivät täsmää";
            }
            setMessage(msg);
        }
    }, [formData, saved, category, user.userType]);

    const toggleShowPass = () => setShowPass((s) => !s);
    const toggleShowPass2 = () => setShowPass2((s) => !s);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSaved(false);
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const submitHandler = (e: React.FormEvent) => {
        e.preventDefault();
        if (user.role !== "senior") {
            dispatch(
                updateGroupRequest({
                    id: formData.id,
                    username: formData.username,
                    password: formData.password,
                    email: formData.username,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    mobileNumber: formData.mobileNumber,
                    nursingHome: formData.nursingHome,
                    department: formData.department,
                    homeAddress: formData.homeAddress,
                    homeCity: formData.homeCity,
                    homePostalCode: formData.homePostalCode,
                    nursingHomeAddress: formData.nursingHomeAddress,
                    role: "senior",
                    relativeCreateEmailId,
                    ...(user.userType === USER_TYPE.appOnly && customerSeniorEmailId && { customerSeniorEmailId }),
                })
            );
            setSaved(true);
            onSelect("laheinen");
        } else {
            if (formData.password && formData.password !== formData.confirmPassword) {
                setMessage("Salasanat eivät täsmää");
                return;
            }
            setMessage("");
            dispatch(
                updateUserRequest({
                    id: formData.id,
                    username: formData.username,
                    password: formData.password,
                    email: formData.username,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    mobileNumber: formData.mobileNumber,
                    nursingHome: formData.nursingHome,
                    department: formData.department,
                    homeAddress: formData.homeAddress,
                    homePostalCode: formData.homePostalCode,
                    homeCity: formData.homeCity,
                    role: "senior",
                })
            );
        }
    };

    const back = () => {
        onSelect("asiakas");
    };

    if (!isLoggedIn) {
        return <Redirect to={localePath("kirjaudu")} />;
    }

    return (
        <Container fluid>
            <Row className="justify-content-md-left">
                <Col xs={12} md={6}>
                    {loading && <Loader />}
                    <h1 className="senior-detail-header">{user.role === "senior" ? "Seniorin tiedot" : "2 Lisää seniori"}</h1>
                    <Form onSubmit={submitHandler} className="user-form">
                        <Form.Group as={Row}>
                            <Col sm={6}>
                                <Form.Label>*Etunimi</Form.Label>
                                <Form.Control
                                    required
                                    className="text-field notranslate"
                                    translate="no"
                                    style={senior?.firstName ? { background: "#d7dadd" } : {}}
                                    type="text"
                                    placeholder="Etunimi"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Label>*Sukunimi</Form.Label>
                                <Form.Control required className="text-field notranslate" translate="no" style={senior?.lastName ? { background: "#d7dadd" } : {}} type="text" placeholder="Sukunimi" name="lastName" value={formData.lastName} onChange={handleChange} />
                            </Col>
                        </Form.Group>

                        {user.userType === USER_TYPE.normal && (
                            <>
                                {user.role === "senior" && (
                                    <>
                                        <Form.Group controlId="senior-password">
                                            <Form.Label>*Salasana</Form.Label>
                                            <InputGroup>
                                                <Form.Control className="notranslate" translate="no" type={showPass ? "text" : "password"} placeholder="Salasana" name="password" value={formData.password} onChange={handleChange} />
                                                <Button variant="outline-secondary shadow-none show-password-btn" onClick={toggleShowPass}>
                                                    <i className={showPass ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                                                </Button>
                                            </InputGroup>
                                            <div className="show-password">Näet salasanasi klikkaamalla silmäkuvaketta</div>
                                            <small>Voit vaihtaa salasanan kirjoittamalla uuden salasanan molempiin kenttiin. Vähintään 11 merkkiä</small>
                                        </Form.Group>
                                        <Form.Group controlId="confirm-password-senior">
                                            <Form.Label>*Toista salasana</Form.Label>
                                            <InputGroup>
                                                <Form.Control className="notranslate" translate="no" type={showPass2 ? "text" : "password"} placeholder="Toista salasana" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} />
                                                <Button variant="outline-secondary shadow-none show-password-btn" onClick={toggleShowPass2}>
                                                    <i className={showPass2 ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                                                </Button>
                                            </InputGroup>
                                            <div className="show-password">Näet salasanasi klikkaamalla silmäkuvaketta</div>
                                            <small>Vaihtaaksesi salasanan kirjoita uusi salasana molempiin kenttiin. Mikäli et halua vaihtaa salasanaa, voit jättää tämän huomiotta.</small>
                                        </Form.Group>
                                    </>
                                )}
                                <Form.Group controlId="senior-mobile">
                                    <Form.Label>Matkapuhelinnumero (vapaaehtoinen)</Form.Label>
                                    <Form.Control
                                        className="text-field notranslate"
                                        translate="no"
                                        style={senior?.mobileNumber ? { background: "#d7dadd" } : {}}
                                        type="text"
                                        name="mobileNumber"
                                        placeholder="Matkapuhelinnumero"
                                        value={formData.mobileNumber}
                                        onChange={handleChange}
                                    />
                                    <small>Esimerkiksi +358 40 123456</small>
                                </Form.Group>

                                <Form.Label>*Osoitetiedot</Form.Label>
                                <Form.Group>
                                    <Form.Check type="radio" label="Asuu kotona" id="homeAddress" name="department" value="homeAddress" checked={formData.department === "homeAddress"} onChange={handleChange} />
                                    <Form.Check type="radio" label="Asuu vanhainkodissa" id="nursingHome" name="department" value="nursingHome" checked={formData.department === "nursingHome"} onChange={handleChange} />
                                </Form.Group>

                                {formData.department === "homeAddress" && (
                                    <>
                                        <Form.Group>
                                            <Form.Label>Katuosoite</Form.Label>
                                            <Form.Control
                                                className="text-field lives-at-home-input notranslate"
                                                translate="no"
                                                style={senior?.homeAddress ? { background: "#d7dadd" } : {}}
                                                type="text"
                                                name="homeAddress"
                                                placeholder="Katuosoite"
                                                value={formData.homeAddress}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Postinumero</Form.Label>
                                            <Form.Control
                                                className="text-field lives-at-home-input notranslate"
                                                translate="no"
                                                style={senior?.homePostalCode ? { background: "#d7dadd" } : {}}
                                                type="text"
                                                name="homePostalCode"
                                                placeholder="Postinumero"
                                                value={formData.homePostalCode}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Kaupunki</Form.Label>
                                            <Form.Control
                                                className="text-field lives-at-home-input notranslate"
                                                translate="no"
                                                style={senior?.homeCity ? { background: "#d7dadd" } : {}}
                                                type="text"
                                                name="homeCity"
                                                placeholder="Kaupunki"
                                                value={formData.homeCity}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                {formData.department === "nursingHome" && (
                                    <>
                                        <Form.Group>
                                            <Form.Label>Vanhainkodin nimi</Form.Label>
                                            <Form.Control
                                                className="text-field lives-at-home-input notranslate"
                                                translate="no"
                                                style={senior?.nursingHome ? { background: "#d7dadd" } : {}}
                                                type="text"
                                                name="nursingHome"
                                                placeholder="Vanhainkodin nimi"
                                                value={formData.nursingHome}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Paikkakunta</Form.Label>
                                            <Form.Control
                                                className="text-field lives-at-home-input notranslate"
                                                translate="no"
                                                style={senior?.nursingHomeAddress ? { background: "#d7dadd" } : {}}
                                                type="text"
                                                name="nursingHomeAddress"
                                                placeholder="Paikkakunta"
                                                value={formData.nursingHomeAddress}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </>
                        )}

                        {user.appOnly && <p style={{ color: "var(--text-primary)" }}>Saat sähköpostiisi seniorin tunnukset (käyttäjänimi ja salasana), joilla voit kirjautua sisään tabletin Digihappy appiin!</p>}

                        <Row className="justify-content-md-center" style={{ minHeight: 100 }}>
                            {user.userType === USER_TYPE.normal && user.role === "customer" ? (
                                <Col xs={6}>
                                    <Button style={{ float: "left" }} className="tallenna user-form-button" onClick={back}>
                                        {`◄ Edellinen`}
                                    </Button>
                                </Col>
                            ) : (
                                <Col xs={6}></Col>
                            )}
                            <Col xs={6}>
                                <Button style={{ float: "right" }} type="submit" className="tallenna senior-button" disabled={btnDisabled}>
                                    {user.role === "senior" ? "Tallenna" : "Seuraava Lisää läheiset ►"}
                                </Button>
                                <div style={{ float: "right" }}>
                                    {error && <Message variant="danger">{error.message}</Message>}
                                    {!error && message && <Message variant="danger">{message}</Message>}
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default SeniorDetail;
